import { z } from "zod";

import { EstadoExpediente } from "./enums";

/**
 * Contrato wire para Server-Sent Events (ADR 018).
 *
 * Principio rector: el evento SSE es una **señal**, no un payload. Lleva el
 * identificador del cambio y un timestamp; el frontend invalida la queryKey
 * correspondiente y TanStack Query refetcha con su propia política. Así
 * preservamos:
 *
 *  - TanStack Query como única fuente de verdad del cache cliente-side.
 *  - Auth + serialización compartidas con los endpoints REST existentes.
 *  - Cero duplicación de DTOs entre "path REST" y "path real-time".
 *
 * Reglas del wire:
 *  - `type` es el discriminator (Zod `discriminatedUnion`).
 *  - Solo viajan **ids + timestamps + clasificadores simples** (estado,
 *    resultado). Nunca payloads ricos.
 *  - `ts` es ISO-8601 para detectar eventos obsoletos si llegan fuera de
 *    orden.
 *
 * Cualquier evento nuevo requiere actualizar también la tabla evento →
 * queryKey en `apps/web/src/lib/realtime/use-sse.ts` y los tests asociados.
 */

const tsSchema = z.string().datetime({ offset: true });

// ─────────────────────────── Eventos de dominio ───────────────────────────

export const expedienteEstadoCambioEventoSchema = z.object({
  type: z.literal("expediente.estado-cambio"),
  expedienteId: z.string().uuid(),
  nuevoEstado: z.nativeEnum(EstadoExpediente),
  ts: tsSchema,
});

export const notificacionNuevaEventoSchema = z.object({
  type: z.literal("notificacion.nueva"),
  notificacionId: z.string().uuid(),
  // `expedienteId` es opcional porque hay notificaciones de sistema que no
  // están atadas a un expediente (ej. cambios de password, bienvenida).
  expedienteId: z.string().uuid().nullable(),
  ts: tsSchema,
});

export const aceptacionRespondidaEventoSchema = z.object({
  type: z.literal("aceptacion.respondida"),
  aceptacionId: z.string().uuid(),
  expedienteId: z.string().uuid(),
  resultado: z.enum(["ACEPTADA", "RECHAZADA"]),
  ts: tsSchema,
});

// ADR 021 — comentarios in-line en archivos LaTeX.
export const comentarioLatexNuevoEventoSchema = z.object({
  type: z.literal("comentario-latex.nuevo"),
  comentarioId: z.string().uuid(),
  proyectoId: z.string().uuid(),
  expedienteId: z.string().uuid(),
  archivoPath: z.string(),
  parentId: z.string().uuid().nullable(),
  ts: tsSchema,
});

export const comentarioLatexResueltoEventoSchema = z.object({
  type: z.literal("comentario-latex.resuelto"),
  comentarioId: z.string().uuid(),
  proyectoId: z.string().uuid(),
  expedienteId: z.string().uuid(),
  ts: tsSchema,
});

// ────────────────────────── Eventos de transporte ──────────────────────────

export const pingEventoSchema = z.object({
  type: z.literal("ping"),
  ts: tsSchema,
});

export const conectadoEventoSchema = z.object({
  type: z.literal("conectado"),
  userId: z.string().uuid(),
  ts: tsSchema,
});

// ───────────────────────────── Union completa ─────────────────────────────

export const eventoSseDominioSchema = z.discriminatedUnion("type", [
  expedienteEstadoCambioEventoSchema,
  notificacionNuevaEventoSchema,
  aceptacionRespondidaEventoSchema,
  comentarioLatexNuevoEventoSchema,
  comentarioLatexResueltoEventoSchema,
]);

export const eventoSseSchema = z.discriminatedUnion("type", [
  expedienteEstadoCambioEventoSchema,
  notificacionNuevaEventoSchema,
  aceptacionRespondidaEventoSchema,
  comentarioLatexNuevoEventoSchema,
  comentarioLatexResueltoEventoSchema,
  pingEventoSchema,
  conectadoEventoSchema,
]);

export type ExpedienteEstadoCambioEvento = z.infer<typeof expedienteEstadoCambioEventoSchema>;
export type NotificacionNuevaEvento = z.infer<typeof notificacionNuevaEventoSchema>;
export type AceptacionRespondidaEvento = z.infer<typeof aceptacionRespondidaEventoSchema>;
export type ComentarioLatexNuevoEvento = z.infer<typeof comentarioLatexNuevoEventoSchema>;
export type ComentarioLatexResueltoEvento = z.infer<typeof comentarioLatexResueltoEventoSchema>;
export type PingEvento = z.infer<typeof pingEventoSchema>;
export type ConectadoEvento = z.infer<typeof conectadoEventoSchema>;

/** Eventos disparados por la lógica de dominio (los que invalidan queries). */
export type EventoSseDominio = z.infer<typeof eventoSseDominioSchema>;

/** Unión completa que viaja por el wire (incluye transporte). */
export type EventoSse = z.infer<typeof eventoSseSchema>;

/** Discriminator literal del `type` — útil para tablas evento → queryKey. */
export type EventoSseTipo = EventoSse["type"];
